import { styled } from '@goshippo/components';
import { NavItem, Navigation } from '@goshippo/libraries/Navigation';

import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { navigationMenuContainer } from '~/src/app/shared/layoutStyles';
import { FeatureFlags, useFeatureFlag } from '~/src/app/utils/featureflag';
import { useDataEntitlements } from '~/src/hooks/useDataEntitlements';
import { useUserEntitlements } from '~/src/hooks/useUserEntitlements';
import { apiConfigNavigationCategory } from '~/src/views/APIConfiguration/APIConfigurationRoutes';
import { createAccountNavigationCategory } from '~/src/views/Account/AccountRoutes';
import { activityNavigationCategory } from '~/src/views/Activity/ActivityRoutes';
import { createSettingsNavigationCategory } from '~/src/views/FulfillmentNetwork/FulfillmentNetworkRoutes';
import { createIntelligenceNavigationCategory } from '~/src/views/Intelligence/IntelligenceRoutes';
import { reconciliationNavigationCategory } from '~/src/views/Reconciliation/ReconciliationRoutes';
import { simulateNavigationCategory } from '~/src/views/Simulate/SimulateRoutes';

const Container = styled('div')`
  ${navigationMenuContainer}
`;

export type NavigationMenuCategory = {
  beta?: boolean;
  icon?: JSX.Element;
  items: NavItem[];
  name: string;
};

const getTranslatedMenuCategory = (
  t: TFunction,
  menuCategory: NavigationMenuCategory,
): NavigationMenuCategory => {
  const translateItem = (item: NavItem) => ({ ...item, name: t(item.name) });

  return {
    icon: menuCategory.icon,
    items: menuCategory.items.map(translateItem),
    name: t(menuCategory.name),
    ...(menuCategory.beta ? { beta: menuCategory.beta } : {}),
  };
};

export const NavigationMenu = () => {
  const {
    entitlements: { hasCustomBranding, isStaff, numUserSeats },
  } = useUserEntitlements();

  const simulationFeatureFlag = useFeatureFlag(FeatureFlags.SIMULATION);
  const simulationEnabled = simulationFeatureFlag?.enabled || false;

  const {
    dataEntitlements: { hasIntelligenceAccess },
  } = useDataEntitlements();

  const intelligenceAdminFeatureFlag = useFeatureFlag(FeatureFlags.INTELLIGENCE_ADMIN);
  const showIntelligenceAdminNav = intelligenceAdminFeatureFlag?.enabled || false;

  const { t } = useTranslation('', { keyPrefix: 'navigationMenu' });
  const { pathname } = useLocation();

  return (
    <Container
      sx={{
        '[id^=nav-cat]': {
          fontWeight: 'bold',
        },
      }}
    >
      <Navigation
        categories={[
          getTranslatedMenuCategory(t, activityNavigationCategory),
          getTranslatedMenuCategory(t, createSettingsNavigationCategory(hasCustomBranding)),
          getTranslatedMenuCategory(t, apiConfigNavigationCategory),
          ...(hasIntelligenceAccess
            ? [
                getTranslatedMenuCategory(
                  t,
                  createIntelligenceNavigationCategory(showIntelligenceAdminNav),
                ),
              ]
            : []),
          getTranslatedMenuCategory(t, createAccountNavigationCategory(numUserSeats)),
          ...(isStaff || simulationEnabled
            ? [getTranslatedMenuCategory(t, simulateNavigationCategory)]
            : []),
          ...(isStaff ? [getTranslatedMenuCategory(t, reconciliationNavigationCategory)] : []),
        ]}
        currentRoute={pathname}
        linkComponent={Link}
      />
    </Container>
  );
};
